import React, { useState, useCallback } from 'react';
import TabOfDataTable from '../../../Elements/DataTable/TabOfDataTable';
import ShownPanelsEnum from '../../../Enums/ShownPanelsEnum';
import GroupMemberSearchParams from './GroupMemberSearchParams';
import GroupMemberItem from '../../../Domain/UserGroup/GroupMemberItem';
import GroupMemberService from '../../../Services/UserGroup/GroupMemberService';
import ButtonTypeEnum from '../../../Enums/ButtonTypeEnum';
import ButtonColorsEnum from '../../../Enums/ButtonColorsEnum';
import IconPositionEnum from '../../../Enums/IconPositionEnum';
import AddGroupMembersWindowDescription from './AddGroupMembersWindowDescription';
import FigmaButtonRow from '../../../Elements/FigmaButtonRow';
import GeneralEditWizzardBase from '../../../Elements/GeneralEditWizzard/GeneralEditWizzardBaseFigma';
import CrudTypesEnum from '../../../Enums/CrudTypesEnum';
import AddGroupMembershipItem from '../../../Domain/UserGroup/AddGroupMembershipItem';
import ErrorMessage from '../../../Elements/ErrorMessage';

const GroupMemberAdmin = ({ groupId }) => {
  const searchParams = createGroupMemberSearchParams();
  const [searchItem, setSearchItem] = useState(searchParams);
  const [shownPanel, setShownPanel] = useState(ShownPanelsEnum.None);
  const [itemList, setItemList] = useState([]);

  GroupMemberItem.removeFunction = clickOnRemoveFunction;

  GroupMemberItem.clickOnSelect = (item) => {
    setItemList((prevList) =>
      prevList.map((element) =>
        element.UserId === item.UserId ? { ...element, Selected: !element.Selected } : element
      )
    );
  };

  GroupMemberItem.clickOnGlobalSelect = () => {
    const selectedStatus = itemList.some((item) => item.Selected === false) ? true : false;
    const templist = [...itemList];

    if (templist !== undefined && templist.length > 0) {
      templist.map((c) => (c.Selected = selectedStatus));
    }
    setItemList(templist);
  };

  GroupMemberItem.selectedStatus = () => {
    return itemList.some((item) => item.Selected === false) ? false : true;
  };

  function clickOnRemoveFunction(item) {
    ErrorMessage.SetApprove('You are about to remove a group member. Are you sure you want to continue?', () => {
      removeMember(item);
    });
  }

  function removeMember(toRemove) {
    GroupMemberService.RemoveGroupMembers([toRemove.UserId], groupId)
      .then((response) => {
        if (response && response.error) {
          ErrorMessage.SetErrorMessage(response.error);
          refresh(false);
        } else {
          ErrorMessage.SetNotification(`Member ${toRemove.Name} #${toRemove.UserId} successfully removed from group.`);
          refresh(true);
        }
      })
      .catch(() => {
        ErrorMessage.SetErrorMessage('Error! ErrorCode: GMA36. Group member remove failed. Please notify IT!');
      });
  }

  const refresh = useCallback(
    (refreshFlag) => {
      if (refreshFlag) {
        setShownPanel(ShownPanelsEnum.None);
        setSearchItem({ ...searchItem, GroupId: groupId });
      } else {
        setShownPanel(ShownPanelsEnum.None);
      }
    },
    [searchItem, groupId]
  );

  const startSearch = useCallback(
    (newSearchItem) => {
      setSearchItem({ ...newSearchItem, GroupId: groupId });
    },
    [groupId]
  );

  const renderOnePanel = (panelId, content) => (shownPanel === panelId ? content : null);

  const renderPanels = () => {
    const addGroupMembershipItem = new AddGroupMembershipItem(groupId);

    return (
      <div>
        {renderOnePanel(
          ShownPanelsEnum.AddNewItem,
          <GeneralEditWizzardBase
            crudType={CrudTypesEnum.Create}
            editedItem={addGroupMembershipItem}
            clickOnCancel={() => refresh(false)}
            clickOnSave={() => refresh(true)}
            windowDescription={AddGroupMembersWindowDescription}
            errorCode={'GMA52'}
          />
        )}
      </div>
    );
  };

  const removeMultipleMembers = (userIds) => {
    GroupMemberService.RemoveGroupMembers(userIds, groupId)
      .then((response) => {
        if (response && response.error) {
          ErrorMessage.SetErrorMessage(response.error);
          refresh(false);
        } else {
          ErrorMessage.SetNotification(`${userIds.length} member(s) successfully removed from group.`);
          refresh(true);
        }
      })
      .catch(() => {
        ErrorMessage.SetErrorMessage('Error! ErrorCode: GMA95. Group members remove failed. Please notify IT!');
      });
  };

  const handleMassRemove = () => {
    const userIdsToRemove = itemList.filter((item) => item.Selected).map((item) => item.UserId);
    if (userIdsToRemove.length === 0) {
      ErrorMessage.SetErrorMessage('No members selected for removal.');
      return;
    }
    ErrorMessage.SetApprove(
      `You are about to remove ${userIdsToRemove.length} selected member(s) from the group. Are you sure you want to continue?`,
      () => {
        removeMultipleMembers(userIdsToRemove);
      }
    );
  };

  const renderButtonsAndPanels = () => (
    <>
      <FigmaButtonRow
        className={'InnerMenuButtonFigmaList'}
        buttons={[
          {
            type: ButtonTypeEnum.NormalNormal,
            color: ButtonColorsEnum.Danger,
            iconPosition: IconPositionEnum.Right,
            onClick: () => handleMassRemove(),
            label: 'Remove',
            tooltip: 'Remove Selected Members',
            roleKey: 'group_delete',
            iconName: 'fa-solid fa-minus-circle',
            disabled: !itemList.some((item) => item.Selected),
          },
          {
            type: ButtonTypeEnum.NormalNormal,
            color: ButtonColorsEnum.Primary,
            iconPosition: IconPositionEnum.Right,
            onClick: () => setShownPanel(ShownPanelsEnum.AddNewItem),
            label: 'Add Member',
            tooltip: 'Add Member',
            roleKey: 'group_create',
            iconName: 'fa-solid fa-plus-circle',
          },
        ]}
      />
      {renderPanels()}
    </>
  );

  return (
    <div className='contentwrapper'>
      {renderButtonsAndPanels()}
      <TabOfDataTable
        itemList={itemList}
        changeOfItemListFunction={setItemList}
        clickOnStartSearch={startSearch}
        searchParamsType={GroupMemberSearchParams}
        searchItem={searchItem}
        orderBy='GroupId'
        showSearchStrip={true}
        serviceFunction={GroupMemberService.GetAllItemsBySearching}
        convertDBItemsFunction={GroupMemberService.ConvertDBItems}
        shownPanel={shownPanel}
        type={GroupMemberItem}
        popupEnabled={true}
        singleSearchLine={true}
        singleSearchLinePlaceholder='Search by ID, name or description'
        errorCode='GMA94'
      />
    </div>
  );

  function createGroupMemberSearchParams() {
    const searchParams = new GroupMemberSearchParams();
    searchParams.GroupId = groupId;
    return searchParams;
  }
};

export default GroupMemberAdmin;
